/**
 * Pure-хелперы ядра редактора записи (задача 77 §1.1).
 *
 *   - `draftFromEntry` — prefill draft из существующей записи (edit-mode);
 *   - `isDraftDirty` — для close-confirm в create-mode;
 *   - `buildPayload` — draft → on-the-wire payload (проект и элемент
 *     необязательны; `assembly_element_id` — из выбранного элемента §4.2);
 *   - `computeLabels` — лейблы pills зафиксированных шагов; `searchPickedById`
 *     ad-hoc-кэш не даёт pill «2. ОБЪЕКТ» упасть в «№ id» (правило «не
 *     показываем технические ID»); элемент резолвится из `elementsById`
 *     (адресный список проекта).
 */
import type {
  Project,
  TimesheetEntry,
  TimesheetEntryPayload,
  WorkType,
} from "@/api/field-timesheet";
import { type Draft, type PillLabels } from "./types";

export function draftFromEntry(entry: TimesheetEntry): Draft {
  const projectId = entry.project_id ?? null;
  const projectCodeRaw = entry.project_code_raw ?? null;
  const assemblyElementId = entry.assembly_element_id ?? null;
  return {
    workTypeId: entry.work_type_id ?? null,
    workTypeText: entry.work_type_text ?? null,
    projectId,
    projectCodeRaw,
    // §20/2.1: оба NULL → «без проекта».
    projectSkipped: projectId === null && projectCodeRaw === null,
    assemblyElementId,
    // Проект известен, но элемента нет → запись сдана «без элемента» (§4.2).
    // Показываем pill «— без элемента», чтобы правка не выглядела как «не
    // выбрано». Для проекта без id (ручной / без проекта) шаг неприменим.
    elementSkipped: projectId !== null && assemblyElementId === null,
    hoursHalf: entry.hours_half,
    reworkNote: entry.rework_note ?? null,
  };
}

export function isDraftDirty(draft: Draft): boolean {
  return (
    draft.workTypeId !== null ||
    draft.workTypeText !== null ||
    draft.projectId !== null ||
    draft.projectCodeRaw !== null ||
    draft.projectSkipped ||
    draft.assemblyElementId !== null ||
    draft.elementSkipped ||
    draft.hoursHalf !== null
  );
}

export function buildPayload(draft: Draft): TimesheetEntryPayload | null {
  if (draft.hoursHalf === null) return null;
  // §20/2.1: проект необязателен — допускаем projectSkipped (оба NULL).
  if (draft.projectId === null && !draft.projectCodeRaw && !draft.projectSkipped) return null;
  if (draft.workTypeId === null && !draft.workTypeText) return null;
  return {
    hours_half: draft.hoursHalf,
    project_id: draft.projectId,
    project_code_raw: draft.projectCodeRaw,
    work_type_id: draft.workTypeId,
    work_type_text: draft.workTypeText,
    // §4.2: элемент сборки из адресного списка проекта; необязателен (NULL).
    assembly_element_id: draft.assemblyElementId,
    // §115/2.1: идентификатор доработки; trim пустой → NULL (backend тоже
    // тримит). Показывается только на закрытой паре, но пронос безусловный —
    // доработочность derived на сервере, поле лишь группирует часы.
    rework_note: draft.reworkNote?.trim() ? draft.reworkNote.trim() : null,
  };
}

export function computeLabels(
  draft: Draft,
  workTypesById: Map<number, WorkType>,
  projectsById: Map<number, Project>,
  searchPickedById: Map<number, string | null>,
  elementsById: Map<number, string>,
  hints: { projectSkipped: string; elementSkipped: string; elementFallback: string },
): PillLabels {
  // Step 1
  let step1: string | null = null;
  if (draft.workTypeId !== null) {
    const wt = workTypesById.get(draft.workTypeId);
    step1 = wt?.name ?? `#${draft.workTypeId}`;
  } else if (draft.workTypeText) {
    step1 = draft.workTypeText;
  }
  // Step 2 — project
  let step2: string | null = null;
  if (draft.projectId !== null) {
    const p = projectsById.get(draft.projectId);
    const cachedCode = searchPickedById.get(draft.projectId);
    step2 = p?.code ?? cachedCode ?? `№ ${draft.projectId}`;
  } else if (draft.projectCodeRaw) {
    step2 = `${draft.projectCodeRaw} ⚠`;
  } else if (draft.projectSkipped) {
    step2 = hints.projectSkipped;
  }
  // Step 3 — assembly element. Имя резолвится из адресного списка проекта;
  // до резолва (или офлайн) — нейтральный fallback, не технический id.
  let step3: string | null = null;
  if (draft.assemblyElementId !== null) {
    step3 = elementsById.get(draft.assemblyElementId) ?? hints.elementFallback;
  } else if (draft.elementSkipped) {
    step3 = hints.elementSkipped;
  }
  return { step1, step2, step3 };
}
